import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Check, ChevronLeft, ChevronRight, CircleAlert, Hash, LockKeyhole, ShieldCheck, X } from "lucide-react";
import ModalScrim from "../components/ModalScrim";
import { Badge, readableGate } from "../lib/ui";
import { advanceGate, assignWave } from "../lib/api";
import RoleHomeShell from "../components/RoleHomeShell";
import StorySpine from "../components/StorySpine";
import { modernizationHealth, useAppGo } from "../lib/story";
import { canSignGates, navItemAllowed } from "../lib/master";
import SubFlowNav, { FocusPanel, MetricStrip } from "../components/SubFlow";
import { subflowHref, useSubFlow } from "../lib/subflow";
import {
  GOVERNANCE_DRILL,
  GOVERNANCE_FLOWS,
  GOVERNANCE_FOCUS_DETAIL,
  GOVERNANCE_FOCUS_TITLE,
} from "../lib/governanceFlows";

export default function Governance({ app, portfolio, onReload, user }) {
  const go = useAppGo(app?.id);
  const location = useLocation();
  const { focus } = useSubFlow("overview");
  const [selected, setSelected] = useState(null);
  const [signoff, setSignoff] = useState(null);
  const [waveOpen, setWaveOpen] = useState(false);
  const gates = useMemo(() => app?.gates || [], [app?.gates]);
  const highlightGate = new URLSearchParams(location.search).get("gate");

  useEffect(() => {
    if (!highlightGate || !gates.length) return;
    const gate = gates.find((g) => g.id === highlightGate);
    if (gate) setSelected(gate);
  }, [highlightGate, gates]);

  if (!app) return null;
  const guide = user?.guide || {};
  const maySign = canSignGates(guide);
  const mayDecide = navItemAllowed("/decision", guide);
  const isOverview = focus === "overview";

  const gov = app.human_governance || {};
  const auth = gov.authorization || {};
  const immut = gov.immutability || {};
  const wave = gov.wave || app.wave || {};
  const intelGates = gov.gates || {};
  const authorized = auth.state === "AUTHORIZED";
  const blockers = gates.filter(g => g.state === "PENDING" || g.state === "IN_REVIEW").length;
  const humanBand = gates.filter(g => ["G1", "G2", "G3", "G4"].includes(g.id));
  const later = gates.filter(g => !["G1", "G2", "G3", "G4"].includes(g.id));
  const versions = immut.versions || app.mdrs || [];
  const audit = gov.audit?.entries || app.audit || [];
  const sealed = gov.audit?.sealed_events || [];

  const health = modernizationHealth({
    authorized,
    waveReady: !!wave.wave_ready,
    planningEligible: !!wave.planning_eligible,
    blockedGates: blockers,
  });

  const toneFor = (state) => (
    state === "PASS" ? "green"
      : state === "READY" ? "amber"
      : state === "IN_REVIEW" || state === "IN_HYPERCARE" ? "cyan"
      : state === "LOCKED" || state === "NOT_APPLICABLE" ? "slate"
      : "slate"
  );

  return (
    <div className="inner-page subflow-page" data-testid="human-governance">
      <RoleHomeShell guide={guide} portfolio={portfolio} app={app} appId={app?.id} count={portfolio?.applications?.length} />

      {!isOverview && (
        <Link to={subflowHref("/governance", app.id, "overview")} className="analytics-back" data-testid="governance-back">
          <ChevronLeft size={16} /> Back to governance overview
        </Link>
      )}

      <FocusPanel
        kicker={`GOVERN · ${app.id}`}
        title={isOverview ? "Modernization health" : (GOVERNANCE_FOCUS_TITLE[focus] || "Governance")}
        detail={GOVERNANCE_FOCUS_DETAIL[focus] || "Not Jira % complete."}
        badge={<Badge tone={blockers > 0 ? "amber" : "green"}>{blockers} open gates</Badge>}
        testId="governance-head"
      />

      {!isOverview && (
        <>
          <SubFlowNav items={GOVERNANCE_FLOWS} focus={focus} appId={app.id} testId="governance-subflow" />
          <StorySpine current="govern" compact />
        </>
      )}

      {isOverview && (
        <>
          <MetricStrip
            items={[
              { label: "Health", value: health.label, testId: "gov-kpi-health" },
              { label: "MDR", value: authorized ? "AUTHORIZED" : "PENDING", testId: "gov-kpi-mdr" },
              { label: "Wave", value: wave.planning_eligible ? "ELIGIBLE" : "BLOCKED", testId: "gov-kpi-wave" },
              { label: "Open gates", value: blockers, testId: "gov-kpi-gates" },
            ]}
          />
          <section className="feature-panel analytics-panel" data-testid="governance-drill">
            <p className="kicker">DRILL DOWN · ONE TOPIC PER SCREEN</p>
            <div className="lifecycle-phase-links">
              {GOVERNANCE_DRILL.map(({ id, label }) => (
                <Link
                  key={id}
                  to={subflowHref("/governance", app.id, id)}
                  className="lifecycle-phase-link"
                  data-testid={`governance-drill-${id}`}
                >
                  <strong>{label}</strong>
                  <span>{GOVERNANCE_FOCUS_DETAIL[id]}</span>
                  <ChevronRight size={16} />
                </Link>
              ))}
            </div>
          </section>
        </>
      )}

      {focus === "health" && (
        <>
          <div className={`health-banner ${health.tone}`} data-testid="modernization-health">
            <div>
              <p className="kicker">MODERNIZATION HEALTH · NOT JIRA % COMPLETE</p>
              <strong>This application is {health.label}</strong>
              <p className="muted small-note">{health.detail}</p>
            </div>
            <Badge tone={health.tone}>{health.label}</Badge>
          </div>
          <div className="gov-contract" data-testid="gov-contract">
            <div>
              <small>MDR AUTHORIZATION</small>
              <strong className={authorized ? "green-text" : "amber-text"}>{auth.state || "REVIEW READY"}</strong>
              <p>{auth.rule}</p>
            </div>
            <div>
              <small>IMMUTABILITY</small>
              <strong>{immut.sealed ? "SEALED" : "DRAFT"}</strong>
              <p>{immut.rule}</p>
            </div>
            <div>
              <small>WAVE ELIGIBILITY</small>
              <strong className={wave.planning_eligible ? "green-text" : "amber-text"}>{wave.planning_eligible ? "PLANNING" : "BLOCKED"}</strong>
              <p>{wave.rule}</p>
            </div>
            <div>
              <small>G4 WAVE READINESS</small>
              <strong className={wave.wave_ready ? "green-text" : "amber-text"}>{wave.wave_ready ? "READY" : "NOT READY"}</strong>
              <p>Eligibility is not G4 PASS. Unknown owners still block sequencing.</p>
            </div>
          </div>
        </>
      )}

      {focus === "mdr" && (
        <section className="feature-panel" data-testid="gov-mdr-panel">
          <div className="panel-header">
            <div>
              <p className="kicker">MDR · AUTHORIZATION & VERSIONING</p>
              <h2>{auth.active?.version || "No MDR"}</h2>
            </div>
            <Badge tone={authorized ? "green" : "amber"}>{authorized ? "IMMUTABLE" : "REVIEW READY"}</Badge>
          </div>
          {authorized ? (
            <div className="mdr-details">
              <div><Hash size={13} /><span data-testid="gov-mdr-hash">{auth.active.hash}</span></div>
              <div><ShieldCheck size={13} /><span>{auth.active.authorized_by} · {auth.active.role}</span></div>
              <div><Check size={13} /><span>{auth.active.authorized_strategy}{auth.active.override ? " · override" : ""}</span></div>
              {auth.active.rationale && <p className="muted small-note">{auth.active.rationale}</p>}
            </div>
          ) : (
            <div className="deployment-note">
              <LockKeyhole size={16} />
              <div>
                <strong>Human authority required</strong>
                <p>{(auth.blockers || []).join(" · ") || "Seal the MDR on 7R Decision. AI and admin-only roles are rejected."}</p>
              </div>
            </div>
          )}
          <div className="mdr-version-list" data-testid="gov-mdr-versions">
            {versions.slice().reverse().map(m => (
              <div key={m.version} className={m.superseded_by ? "mdr-item superseded" : "mdr-item"}>
                <strong>{m.version}</strong>
                <span>{(m.state || "").replaceAll("_", " ")}</span>
                {m.hash && <small>{m.hash}</small>}
                {m.authorized_by && <small>{m.authorized_by} · {String(m.authorized_at || "").slice(0, 10)}</small>}
                {m.superseded_by && <em>Superseded by {m.superseded_by}</em>}
              </div>
            ))}
          </div>
          {maySign && mayDecide && (!authorized ? (
            <button type="button" className="primary-button full" data-testid="gov-go-authorize" onClick={() => go("/decision?focus=authorize")}>
              <ShieldCheck size={16} /> Authorize MDR
            </button>
          ) : (
            <button type="button" className="secondary-button full" data-testid="gov-go-reassess" onClick={() => go("/decision?focus=authorize")}>
              Material change · create successor
            </button>
          ))}
        </section>
      )}

      {focus === "wave" && (
        <section className="feature-panel" data-testid="gov-wave-panel" id="spine-plan">
          <div className="panel-header">
            <div>
              <p className="kicker">WAVE ELIGIBILITY ≠ G4</p>
              <h2>{wave.id || "Unassigned"}</h2>
              {app.wave?.preview && (
                <p className="muted small-note" data-testid="wave-seeded-preview">Seeded preview — MDR not authorized. Live assignment still requires POST /wave after seal.</p>
              )}
            </div>
            <Badge tone={wave.planning_eligible ? "green" : "amber"}>{wave.planning_eligible ? "ELIGIBLE" : "NOT ELIGIBLE"}</Badge>
          </div>
          <p className="muted small-note">{wave.reason}</p>
          {wave.name && <p><b>{wave.name}</b> · {wave.owner} · {wave.target}</p>}
          <p className="muted small-note">{wave.status}</p>
          {app.wave_eligible && (
            <div className="wave-eligible-badge" data-testid="gov-wave-eligible-badge">
              <ShieldCheck size={13} /> Wave planning eligible
            </div>
          )}
          {(intelGates.G4?.blockers || []).length > 0 && (
            <div className="gov-blockers">
              {intelGates.G4.blockers.map(b => <Badge key={b} tone="amber">{b}</Badge>)}
            </div>
          )}
          {maySign && (
            <button
              type="button"
              className="secondary-button full"
              data-testid="gov-assign-wave"
              disabled={!wave.planning_eligible}
              onClick={() => setWaveOpen(true)}
            >
              {wave.planning_eligible ? "Confirm wave assignment" : "Draft cannot enter wave"}
            </button>
          )}
        </section>
      )}

      {focus === "gates-early" && (
        <section className="feature-panel" data-testid="gov-g1g4" id="spine-govern">
          <div className="panel-header">
            <div>
              <p className="kicker">G1–G4 · HUMAN AUTHORIZATION BAND</p>
              <h2>Assessment · Decision · Architecture · Wave</h2>
            </div>
            <Badge tone="cyan">Protected</Badge>
          </div>
          <div className="gov-gate-grid">
            {["G1", "G2", "G3", "G4"].map(id => {
              const intel = intelGates[id] || {};
              const stored = gates.find(g => g.id === id) || {};
              return (
                <div key={id} className={`gov-gate-card ${stored.state === "PASS" ? "pass" : ""}`} data-testid={`gov-card-${id.toLowerCase()}`}>
                  <small>{id} · {intel.lifecycle || stored.authority}</small>
                  <strong>{intel.name || stored.name}</strong>
                  <Badge tone={toneFor(stored.state)}>{readableGate(stored.state)}</Badge>
                  <p>{intel.next || stored.detail}</p>
                  {(intel.blockers || []).map(b => <em key={b}>{b}</em>)}
                  {id === "G2" && !authorized && maySign && mayDecide && (
                    <button type="button" className="text-button" onClick={() => go("/decision?focus=authorize")}>Seal MDR <ChevronRight size={13} /></button>
                  )}
                  {maySign && ["G3", "G4"].includes(id) && authorized && stored.state !== "PASS" && stored.state !== "NOT_APPLICABLE" && (
                    <button type="button" className="secondary-button small-btn" data-testid={`gov-signoff-${id.toLowerCase()}`} onClick={() => setSignoff(id)}>
                      <ShieldCheck size={14} /> Sign off {id}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          <GateDetailList
            gates={humanBand}
            intelGates={intelGates}
            selected={selected}
            setSelected={setSelected}
            toneFor={toneFor}
            maySign={maySign}
            authorized={authorized}
            setSignoff={setSignoff}
            highlightGate={highlightGate}
          />
        </section>
      )}

      {focus === "gates-late" && (
        <>
          <section className="feature-panel" data-testid="gov-g5g8">
            <div className="panel-header">
              <div>
                <p className="kicker">G5–G8 · DELIVERY BAND</p>
                <h2>Security · Test · GO · Hypercare · Success</h2>
              </div>
              <Badge tone="cyan">Protected</Badge>
            </div>
            <p className="muted small-note">G5 + G6 must be valid before G7. G8 cannot PASS because deployment succeeded alone.</p>
            <p className="muted small-note" data-testid="gov-g7-owner-cue">G7 owner is {(intelGates.G7?.pending_by) || "Elena Ruiz · Business Owner"}. Product Owner may still POST deploy (freeze).</p>
            <p className="muted small-note" data-testid="gov-g8-owner-cue">G8 owner is {(intelGates.G8?.pending_by) || "Elena Ruiz · Business Owner"}. Business validation closes production success.</p>
          </section>
          <GateDetailList
            gates={later}
            intelGates={intelGates}
            selected={selected}
            setSelected={setSelected}
            toneFor={toneFor}
            maySign={maySign}
            authorized={authorized}
            setSignoff={setSignoff}
            highlightGate={highlightGate}
          />
          <div className="governance-footer">
            <div>
              <LockKeyhole size={17} />
              <strong>Protected invariants</strong>
              <p>G1 records WHY. G2 seals the MDR. G3–G4 require that seal. G5 + G6 must be valid before G7. G8 cannot PASS because deployment succeeded.</p>
            </div>
          </div>
        </>
      )}

      {focus === "audit" && (
        <section className="feature-panel audit-panel">
          <div className="panel-header">
            <div>
              <p className="kicker">AUDIT TRAIL · WHO · WHAT · WHY</p>
              <h2>Traceable governance memory</h2>
            </div>
            <Badge tone="cyan">TENANT SCOPED</Badge>
          </div>
          {sealed.length > 0 && (
            <div className="sealed-audit" data-testid="gov-sealed-audit">
              {sealed.map(e => (
                <div key={e.event_id} className="audit-row sealed">
                  <span>{e.created_at}</span>
                  <div>
                    <strong>{e.version} sealed</strong>
                    <p>{e.authorized_by} · {e.hash}</p>
                  </div>
                  <Hash size={15} />
                </div>
              ))}
            </div>
          )}
          {audit.map((item, i) => (
            <div key={i} className="audit-row" data-testid={`audit-row-${i}`}>
              <span>{item.time}</span>
              <div>
                <strong>{item.actor}</strong>
                <p>{item.action} · {item.detail}</p>
              </div>
              <Check size={15} />
            </div>
          ))}
        </section>
      )}

      {signoff && <GateSignoffModal gateId={signoff} appId={app.id} onClose={() => setSignoff(null)} onDone={async () => { setSignoff(null); if (onReload) await onReload(); }} />}
      {waveOpen && <WaveModal app={app} wave={wave} onClose={() => setWaveOpen(false)} onDone={async () => { setWaveOpen(false); if (onReload) await onReload(); }} />}
    </div>
  );
}

function GateDetailList({ gates, intelGates, selected, setSelected, toneFor, maySign, authorized, setSignoff, highlightGate }) {
  return (
    <>
      <div className="gate-list" data-testid="governance-gate-list">
        {gates.map(g => (
          <button
            key={g.id}
            data-testid={`gate-row-${g.id.toLowerCase()}`}
            className={`gate-row ${g.state === "PASS" ? "gate-pass" : ""} ${selected?.id === g.id ? "gate-open" : ""} ${highlightGate === g.id ? "gate-queue-focus" : ""}`}
            onClick={() => setSelected(selected?.id === g.id ? null : g)}
          >
            <div className="gate-id">{g.state === "PASS" ? <Check size={17} /> : <span>{g.id}</span>}</div>
            <div className="gate-content">
              <strong>{g.id} — {g.name}</strong>
              <span>{(intelGates[g.id] && intelGates[g.id].next) || g.detail}</span>
            </div>
            <Badge tone={toneFor(g.state)}>{readableGate(g.state)}</Badge>
            <ChevronRight size={16} />
          </button>
        ))}
      </div>
      {selected && (
        <section className="feature-panel gate-detail" data-testid={`gate-detail-${selected.id.toLowerCase()}`}>
          <div className="panel-header">
            <div>
              <p className="kicker">GATE DETAIL · POLICY {selected.policy}</p>
              <h2>{selected.id} — {selected.name}</h2>
              <p className="muted">Authority: <b>{selected.authority}</b></p>
              {intelGates[selected.id]?.pending_by && (
                <p className="muted small-note" data-testid={`gate-owner-cue-${selected.id.toLowerCase()}`}>
                  Queue owner: <b>{intelGates[selected.id].pending_by}</b>
                </p>
              )}
            </div>
            <Badge tone={selected.state === "PASS" ? "green" : "amber"}>{readableGate(selected.state)}</Badge>
          </div>
          {(intelGates[selected.id]?.blockers || []).length > 0 && (
            <div className="gov-blockers">
              {intelGates[selected.id].blockers.map(b => (
                <div key={b} className="evidence-line warning"><CircleAlert size={14} /> {b}</div>
              ))}
            </div>
          )}
          <div className="gate-criteria">
            <h4>Evidence required</h4>
            {(selected.criteria || intelGates[selected.id]?.criteria || []).length === 0 && <p className="muted small-note">No structured criteria recorded for this gate in the demo dataset.</p>}
            {(selected.criteria || intelGates[selected.id]?.criteria || []).map(c => (
              <div key={c} className="evidence-line"><Check size={14} /> {c}</div>
            ))}
          </div>
          {maySign && ["G3", "G4", "G5", "G6"].includes(selected.id) && authorized && selected.state !== "PASS" && selected.state !== "NOT_APPLICABLE" && (
            <button type="button" className="primary-button" data-testid={`gov-detail-signoff-${selected.id.toLowerCase()}`} onClick={() => setSignoff(selected.id)}>
              <ShieldCheck size={16} /> Sign off {selected.id}
            </button>
          )}
          <div className="policy-note">
            <LockKeyhole size={14} />
            <span>Protected gates<br /><b>Only named human authority can pass, reject or waive · AI is advisory</b></span>
          </div>
        </section>
      )}
    </>
  );
}

function GateSignoffModal({ gateId, appId, onClose, onDone }) {
  const [name, setName] = useState("Alex Morgan");
  const [role, setRole] = useState(gateId === "G4" ? "Modernization Lead" : "Enterprise Architect");
  const [note, setNote] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await advanceGate(appId, gateId, { approver: name, role, note });
      await onDone();
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Sign-off failed");
    } finally { setBusy(false); }
  };
  return (
    <ModalScrim>
      <form className="modal wide" onSubmit={submit}>
        <button type="button" className="modal-close" onClick={onClose}><X size={18} /></button>
        <p className="kicker">GATE SIGN-OFF · {gateId}</p>
        <h2>Confirm {gateId} evidence</h2>
        <p className="muted">Only a configured decision authority can advance a protected gate. AI or admin-only roles are rejected.</p>
        <label>Approver<input data-testid={`gov-signoff-name-${gateId}`} required value={name} onChange={e => setName(e.target.value)} /></label>
        <label>Authority role
          <select data-testid={`gov-signoff-role-${gateId}`} value={role} onChange={e => setRole(e.target.value)}>
            {["Enterprise Architect", "Solution Architect", "Security Architect", "Product Owner", "Business Owner", "Modernization Lead"].map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </label>
        <label>Evidence note<textarea data-testid={`gov-signoff-note-${gateId}`} rows={2} value={note} onChange={e => setNote(e.target.value)} /></label>
        {err && <div className="error-message">{err}</div>}
        <button data-testid={`gov-confirm-signoff-${gateId}`} className="primary-button full" type="submit" disabled={busy}>{busy ? "Signing off…" : `Confirm ${gateId} PASS`}</button>
      </form>
    </ModalScrim>
  );
}

function WaveModal({ app, wave, onClose, onDone }) {
  const [waveId, setWaveId] = useState(wave.id || "WAVE-03");
  const [name, setName] = useState(wave.name || "");
  const [owner, setOwner] = useState(wave.owner || "");
  const [target, setTarget] = useState(wave.target || "");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await assignWave(app.id, { wave_id: waveId, name, owner, target });
      await onDone();
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Wave assignment failed");
    } finally { setBusy(false); }
  };
  return (
    <ModalScrim>
      <form className="modal" onSubmit={submit}>
        <button type="button" className="modal-close" onClick={onClose}><X size={18} /></button>
        <p className="kicker">WAVE ASSIGNMENT · AUTHORIZED MDR REQUIRED</p>
        <h2>Assign {app.id} to a wave</h2>
        <p className="muted">Draft MDRs are rejected. A wave earlier than a must-move dependency is rejected. G4 readiness is separate.</p>
        <label>Wave id<input data-testid="gov-wave-id" required value={waveId} onChange={e => setWaveId(e.target.value)} /></label>
        <label>Name<input value={name} onChange={e => setName(e.target.value)} /></label>
        <label>Owner<input value={owner} onChange={e => setOwner(e.target.value)} /></label>
        <label>Target<input value={target} onChange={e => setTarget(e.target.value)} /></label>
        {err && <div className="error-message" data-testid="gov-wave-error">{err}</div>}
        <button data-testid="gov-confirm-wave" className="primary-button full" type="submit" disabled={busy}>{busy ? "Assigning…" : "Assign to wave"}</button>
      </form>
    </ModalScrim>
  );
}
